import { useMutation } from '@tanstack/react-query';
import type { SubjectKind } from '@hp/domain';
import { invalidatePipelineState } from '../pipeline/queries';
import { trpcClient } from '../../api/trpc';
import { invalidateReview, type ClusterListItem } from './queries';

/** Bulk-approve requires an entityType — groups the selected clusters by theirs. */
export function useBulkApprove(itemById: Map<number, ClusterListItem>, onApproved: (approved: number) => void) {
  return useMutation({
    mutationFn: async (ids: number[]) => {
      // bulk-approve requires an entityType — group selected clusters by theirs
      const byType = new Map<SubjectKind, number[]>();
      for (const id of ids) {
        const item = itemById.get(id);
        if (!item) continue;
        const list = byType.get(item.entityType) ?? [];
        list.push(id);
        byType.set(item.entityType, list);
      }
      let approved = 0;
      for (const [entityType, clusterIds] of byType) {
        const res = await trpcClient.review.bulkApprove.mutate({ entityType, clusterIds });
        approved += res.approved;
      }
      return approved;
    },
    onSuccess: (approved) => {
      onApproved(approved);
      return Promise.all([invalidateReview(), invalidatePipelineState()]);
    },
  });
}

/** Approve every pending auto cluster server-side — one call per entity type ('' = both). */
export function useApproveAllAuto(onApproved: (approved: number) => void) {
  return useMutation({
    mutationFn: async (entityType: SubjectKind | '') => {
      const types: SubjectKind[] = entityType ? [entityType] : ['organization', 'person'];
      let approved = 0;
      for (const t of types) approved += (await trpcClient.review.approveAllAuto.mutate({ entityType: t })).approved;
      return approved;
    },
    onSuccess: (approved) => {
      onApproved(approved);
      return Promise.all([invalidateReview(), invalidatePipelineState()]);
    },
  });
}
